import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cable, Plus, Save, Wifi, X } from 'lucide-react';
import type { OnuPort, OnuVlan, Page, WanCandidate } from '@simpleacs/shared';
import { Button } from '../../components/ui';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { portChanges, portName, portOnWan, portsAddable, portsOnOffer, runWanPorts, wanPortTarget, wanPortsBlocked } from '../../lib/wan-ports';

/**
 * The ports that ride one WAN, chosen on its card (operator, 2026-10-14: "in
 * the wan card, allow choosing ports associated with the wan interface").
 * The same records the Ports tab edits — the same queries, the same PUT — so
 * the two can never disagree (`lib/wan-ports.ts`). Saved in the tasks tray.
 */
export function WanPorts({
  connection,
  base,
  serial,
  editable,
  href,
}: {
  connection: WanCandidate;
  /** The owner's config root, `/devices/<id>` — the Ports tab's own. */
  base: string;
  serial: string;
  editable: boolean;
  href?: string | undefined;
}) {
  const tasks = useTasks();
  const target = wanPortTarget(connection);
  const portsQuery = useQuery({
    queryKey: ['onu-config', base, 'ports'],
    queryFn: () => api.get<Page<OnuPort>>(`${base}/ports`),
    enabled: target !== null,
  });
  const vlansQuery = useQuery({
    queryKey: ['onu-config', base, 'vlans'],
    queryFn: () => api.get<Page<OnuVlan>>(`${base}/vlans`),
    enabled: target !== null,
  });
  // Networks that are off are not offered: they ride no WAN until switched on (the Ports tab names them).
  const ports = useMemo(() => portsOnOffer(portsQuery.data?.items ?? []), [portsQuery.data]);
  const current = useMemo(
    () => new Set(target ? ports.filter((p) => portOnWan(p, target)).map((p) => p.port) : []),
    [ports, target],
  );
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [adding, setAdding] = useState(false);
  const chosen = draft ?? current;

  if (!target) return null;
  const blocked = wanPortsBlocked(target, vlansQuery.data?.items ?? []);
  const changes = portChanges(ports, target, chosen);
  const routed = target.kind === 'lan';
  const canEdit = editable && blocked === null;
  /*
    Only the ports ON this WAN are drawn; a "+" offers the ones no other bridge
    holds (operator, 2026-10-28: "do not show ports not being used in a wan
    interface, instead, put a plus sign to add an interface if it hasn't
    already been assigned to another interface"). The LAN only takes ports —
    one leaves it by being given to a bridge — so its card has no "+" and no
    "×"; a bridge's "×" sends the port back to the LAN.
  */
  const bound = ports.filter((p) => chosen.has(p.port));
  const addable = portsAddable(ports, target).filter((p) => !chosen.has(p.port));

  const add = (port: string) => {
    const next = new Set(chosen);
    next.add(port);
    setDraft(next);
    setAdding(false);
  };
  const remove = (port: string) => {
    const next = new Set(chosen);
    next.delete(port);
    setDraft(next);
  };
  const save = () => {
    const sent = changes;
    setDraft(null);
    tasks.start({
      label: `Ports on ${connection.label || 'the WAN'} — ${serial}`,
      ...(href ? { href } : {}),
      run: () => runWanPorts(base, target, sent, api),
      done: (r) => r.message,
      invalidate: [
        ['onu-config', base, 'ports'],
        ['onu-config', base, 'config-plan'],
        ['device'],
      ],
    });
  };

  return (
    <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-2.5">
      <span className="pt-1 text-sm text-muted">Ports</span>
      <div className="space-y-2">
        {portsQuery.isLoading ? (
          <span className="text-xs text-faint">Reading the ports…</span>
        ) : ports.length === 0 ? (
          <span className="text-xs text-faint">This ONU reports no ports.</span>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {bound.length === 0 && (
                <span className="text-xs text-faint">{routed ? 'No port on the LAN.' : 'No port on this bridge.'}</span>
              )}
              {bound.map((p) => {
                const Icon = p.kind === 'wifi' ? Wifi : Cable;
                return (
                  <span
                    key={p.port}
                    className="flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-2 py-1 text-xs text-ink"
                  >
                    <Icon className="size-3.5" />
                    {portName(p)}
                    {p.kind === 'wifi' && p.network?.band ? <span className="text-faint">{p.network.band}</span> : null}
                    {canEdit && !routed && (
                      <button
                        type="button"
                        className="ml-0.5 text-muted hover:text-ink"
                        title="Back to the LAN"
                        aria-label={`Take ${portName(p)} off this WAN`}
                        onClick={() => remove(p.port)}
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </span>
                );
              })}
              {canEdit && addable.length > 0 && (
                <button
                  type="button"
                  className={`flex items-center gap-1 rounded-lg border border-dashed px-2 py-1 text-xs ${
                    adding ? 'border-primary/40 text-ink' : 'border-line text-muted hover:text-ink'
                  }`}
                  title="Add a port to this WAN"
                  aria-label="Add a port to this WAN"
                  aria-expanded={adding}
                  onClick={() => setAdding((v) => !v)}
                >
                  <Plus className="size-3.5" />
                </button>
              )}
            </div>
            {adding && addable.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {addable.map((p) => {
                  const Icon = p.kind === 'wifi' ? Wifi : Cable;
                  return (
                    <button
                      type="button"
                      key={p.port}
                      className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2 py-1 text-xs text-muted hover:text-ink"
                      onClick={() => add(p.port)}
                    >
                      <Icon className="size-3.5" />
                      {portName(p)}
                      {p.kind === 'wifi' && p.network?.band ? <span className="text-faint">{p.network.band}</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
        <p className="text-xs text-faint">
          {blocked ??
            (routed
              ? 'Ports on the router’s own LAN; a port leaves it by being given to a bridge.'
              : `Ports bridged on VLAN ${target.kind === 'bridge' ? target.vlanId : ''}. Taking one off puts it back on the LAN — a WiFi network is switched off on the way, so an open one never lands on the subscriber’s own network.`)}
        </p>
        {editable && changes.length > 0 && (
          <div className="flex gap-2">
            <Button size="sm" variant="primary" icon={<Save className="size-3.5" />} onClick={save}>
              Save ports
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Undo
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
